import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/shared/Layout';
import { vacationAPI, leaveAPI, holidayAPI } from '../services/api';
import UpcomingEvents from '../components/shared/UpcomingEvents';
import TodayAbsences  from '../components/shared/TodayAbsences';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Palmtree, ClipboardList, CheckCircle2, XCircle, X, MessageSquare } from 'lucide-react';

function useActiveTab() {
  const { pathname } = useLocation();
  if (pathname.includes('/permisos')) return 'leaves';
  return 'vacations';
}

function DecisionModal({ item, type, onClose, onDone }) {
  const [decision, setDecision] = useState('');
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(false);
  const [holidays, setHolidays] = useState([]);

  // Verificar feriados en el rango (solo para vacaciones)
  React.useEffect(() => {
    if (type === 'vacation' && item.start_date && item.end_date) {
      holidayAPI.checkRange({ start_date: item.start_date, end_date: item.end_date })
        .then(r => setHolidays(r.data.holidays || []))
        .catch(() => {});
    } else if (type === 'leave' && item.request_date) {
      const d = item.request_date.split?.('T')[0] || item.request_date;
      holidayAPI.checkRange({ start_date: d, end_date: d })
        .then(r => setHolidays(r.data.holidays || []))
        .catch(() => {});
    }
  }, [type, item]);

  useEffect(() => {
    if (type === 'leave' && holidays.length > 0) {
      setDecision('rejected');
      setComments('Rechazado por feriado');
    }
  }, [type, holidays]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!decision) return toast.error('Selecciona una decisión');
    setLoading(true);
    try {
      if (type === 'vacation') await vacationAPI.process(item.id, { decision, comments });
      else                     await leaveAPI.process(item.id, { decision, comments });
      toast.success(decision === 'approved' ? 'Solicitud aprobada' : 'Solicitud rechazada');
      onDone(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al procesar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">Procesar solicitud</div>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{item.first_name} {item.last_name}</div>
              {type === 'vacation' ? (
                <div style={{ fontSize: 14, color: 'var(--gray-600)' }}>
                  Vacaciones: {format(new Date(item.start_date), 'dd/MM/yyyy')} → {format(new Date(item.end_date), 'dd/MM/yyyy')} ({item.total_days} {item.total_days === 1 ? 'día' : 'días'})
                  <div style={{ marginTop: 4, color: 'var(--gray-500)' }}>Notas: {item.notes}</div>
                </div>
              ) : (
                <div style={{ fontSize: 14, color: 'var(--gray-600)' }}>
                  Permiso: {format(new Date(item.request_date), 'dd/MM/yyyy')} — {item.days === 1 ? 'Día completo' : item.half_day_period === 'morning' ? 'Medio día (mañana)' : item.half_day_period === 'afternoon' ? 'Medio día (tarde)'
                          : `${item.days}d`}
                  <div style={{ marginTop: 4, color: 'var(--gray-500)' }}>Notas: {item.reason}</div>
                </div>
              )}
            </div>

            {/* Aviso de feriados */}
            {type === 'vacation' && holidays.length > 0 && (
              <div style={{
                padding: '10px 14px', borderRadius: 8, marginBottom: 14,
                background: '#fffbeb', border: '1px solid #fcd34d',
              }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#92400e', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>⚠️</span> Feriado(s) dentro del período solicitado
                </div>
                <div style={{ fontSize: 12, color: '#78350f', marginBottom: 6 }}>
                  Los siguientes días festivos caen en el rango de vacaciones:
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#92400e' }}>
                  {holidays.map(h => (
                    <li key={h.date}>
                      <strong>{new Date(h.date + 'T00:00:00').toLocaleDateString('es', { day: '2-digit', month: 'long', year: 'numeric' })}</strong> — {h.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {type === 'leave' && holidays.length > 0 && (
              <div style={{
                padding: '10px 14px', borderRadius: 8, marginBottom: 14,
                background: '#fffbeb', border: '1px solid #fcd34d',
              }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#92400e', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>⚠️</span> Feriado(s) dentro del período solicitado
                </div>
                <div style={{ fontSize: 12, color: '#78350f', marginBottom: 6 }}>
                  Los siguientes días festivos caen en el rango de permisos:
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#92400e' }}>
                  {holidays.map(h => (
                    <li key={h.date}>
                      <strong>{new Date(h.date + 'T00:00:00').toLocaleDateString('es', { day: '2-digit', month: 'long', year: 'numeric' })}</strong> — {h.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Decisión *</label>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button"
                  className={`btn ${decision === 'approved' ? 'btn-success' : 'btn-secondary'}`}
                  style={{ flex: 1, justifyContent: 'center', gap: 6, opacity: type === 'leave' && holidays.length > 0 ? 0.4 : 1 }}
                  disabled={type === 'leave' && holidays.length > 0}
                  onClick={() => type === 'leave' && holidays.length > 0 ? null : setDecision('approved')}>
                  <CheckCircle2 size={15} /> Aprobar
                </button>
                <button type="button"
                  className={`btn ${decision === 'rejected' ? 'btn-danger' : 'btn-secondary'}`}
                  style={{ flex: 1, justifyContent: 'center', gap: 6 }}
                  onClick={() => setDecision('rejected')}>
                  <XCircle size={15} /> Rechazar
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <MessageSquare size={13} /> Comentarios {decision === 'rejected' ? '*' : '(opcional)'}
              </label>
              <textarea className="form-input form-textarea"
                placeholder={decision === 'rejected' ? 'Indica el motivo del rechazo...' : 'Comentarios adicionales...'}
                value={comments} onChange={e => setComments(e.target.value)}
                required={decision === 'rejected'} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !decision}>
              {loading ? 'Procesando…' : 'Confirmar decisión'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SupervisorPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const tab = useActiveTab();
  const [vacPending,   setVacPending]   = useState([]);
  const [leavePending, setLeavePending] = useState([]);
  const [selected,     setSelected]     = useState(null);
  const [selectedType, setSelectedType] = useState('vacation');
  const [loadingData,  setLoadingData]  = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoadingData(true);
    const [vRes, lRes] = await Promise.all([
      vacationAPI.getPending().catch(() => ({ data: [] })),
      leaveAPI.getPending().catch(() => ({ data: [] })),
    ]);
    setVacPending(vRes.data);
    setLeavePending(lRes.data);
    setLoadingData(false);
  };

  const tabStyle = (t) => ({
    padding: '9px 20px', borderRadius: 8, cursor: 'pointer',
    fontWeight: 500, fontSize: 14, border: 'none', fontFamily: 'var(--font)',
    background: tab === t ? 'var(--primary)' : 'var(--gray-100)',
    color: tab === t ? 'white' : 'var(--gray-700)',
    transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 7,
  });

  const Badge = ({ count }) => count > 0 ? (
    <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: 10, padding: '1px 7px', fontSize: 12, fontWeight: 700 }}>
      {count}
    </span>
  ) : null;

  const EmptyState = ({ label }) => (
    <div className="empty-state">
      <CheckCircle2 size={40} color="var(--gray-300)" style={{ margin: '0 auto 12px', display: 'block' }} />
      <div className="empty-state-title">¡Todo al día!</div>
      <div>No hay {label} pendientes</div>
    </div>
  );

  return (
    <Layout title="Panel de supervisor">
      <div className="page-header">
        <div>
          <div className="page-title">Panel de supervisor</div>
          <div className="page-subtitle">
            {loadingData ? 'Cargando…' : `${vacPending.length + leavePending.length} solicitudes pendientes`}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button style={tabStyle('vacations')} onClick={() => navigate('/supervisor/vacaciones')}>
          <Palmtree size={15} strokeWidth={1.8} /> Vacaciones <Badge count={vacPending.length} />
        </button>
        <button style={tabStyle('leaves')} onClick={() => navigate('/supervisor/permisos')}>
          <ClipboardList size={15} strokeWidth={1.8} /> Permisos <Badge count={leavePending.length} />
        </button>
      </div>

      {tab === 'vacations' && (
        <div className="card">
          <div className="card-header"><div className="card-title">Solicitudes de vacaciones pendientes</div></div>
          {loadingData ? <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray-500)' }}>Cargando…</div>
          : vacPending.length === 0 ? <EmptyState label="solicitudes de vacaciones" />
          : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Empleado</th><th>Depto.</th><th>Desde</th><th>Hasta</th><th>Días</th><th>Notas</th><th>Solicitud</th><th></th></tr>
                </thead>
                <tbody>
                  {vacPending.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 500 }}>{r.first_name} {r.last_name}</td>
                      <td style={{ color: 'var(--gray-500)', fontSize: 13 }}>{r.department || '—'}</td>
                      <td>
                        {r.start_date.slice(0, 10).split('-').reverse().join('/')}
                      </td>
                      <td>
                        {r.end_date.slice(0, 10).split('-').reverse().join('/')}
                      </td>
                      <td><strong>{r.total_days}</strong></td>
                      <td style={{ maxWidth: 140, color: 'var(--gray-500)', fontSize: 13 }} className="truncate">{r.notes || '—'}</td>
                      <td style={{ color: 'var(--gray-500)', fontSize: 13 }}>{format(new Date(r.created_at), 'dd/MM/yyyy')}</td>
                      <td>
                        <button className="btn btn-primary btn-sm"
                          onClick={() => { setSelected(r); setSelectedType('vacation'); }}>
                          Revisar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'leaves' && (
        <div className="card">
          <div className="card-header"><div className="card-title">Solicitudes de permisos de día libre pendientes</div></div>
          {loadingData ? <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray-500)' }}>Cargando…</div>
          : leavePending.length === 0 ? <EmptyState label="permisos" />
          : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Empleado</th><th>Depto.</th><th>Fecha</th><th>Duración / Turno</th><th>Notas</th><th>Solicitud</th><th></th></tr>
                </thead>
                <tbody>
                  {leavePending.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 500 }}>{r.first_name} {r.last_name}</td>
                      <td style={{ color: 'var(--gray-500)', fontSize: 13 }}>{r.department || '—'}</td>
                      <td style={{ whiteSpace:'nowrap' }}>
                        {r.request_date.slice(0, 10).split('-').reverse().join('/')}
                      </td>
                      <td>
                        <strong>
                          { parseFloat(r.days) === 1 ? 'Día completo'
                          : r.half_day_period === 'morning' ? 'Medio día (mañana)'
                          : r.half_day_period === 'afternoon' ? 'Medio día (tarde)'
                          : `${r.days}d` }
                        </strong>
                      </td>
                      <td style={{ maxWidth: 140, color: 'var(--gray-500)', fontSize: 13 }} className="truncate">{r.reason || '—'}</td>
                      <td style={{ color: 'var(--gray-500)', fontSize: 13 }}>{format(new Date(r.created_at), 'dd/MM/yyyy')}</td>
                      <td>
                        <button className="btn btn-primary btn-sm"
                          onClick={() => { setSelected(r); setSelectedType('leave'); }}>
                          Revisar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {selected && (
        <DecisionModal item={selected} type={selectedType}
          onClose={() => setSelected(null)} onDone={loadData} />
      )}
      <TodayAbsences supervisorUserId={user?.id} />
      <UpcomingEvents />
    </Layout>
  );
}
